import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "general",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill out all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Thanks — we'll be in touch!");
      setForm({
        name: "",
        email: "",
        phone: "",
        interest: "general",
        subject: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err.message ||
        "Unable to submit right now — please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Info column */}
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-coral mb-4">
              Connect with us
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.05] tracking-tight text-brand-navy">
              Questions?{" "}
              <span className="text-brand-coral">Start a conversation.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Whether you&apos;re a parent, a prospective sponsor, a potential
              volunteer, or a local partner — we&apos;d love to hear from you.
            </p>

            <div className="mt-10 space-y-5">
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-start gap-4"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-navy text-white flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-brand-navy">Our location</div>
                  <div className="text-slate-600">
                    10950 Arrow Route #1203
                    <br />
                    Rancho Cucamonga, CA 91729
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="flex items-start gap-4"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-coral text-white flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-brand-navy">Email us</div>
                  <div className="text-slate-600">Karlynncammon@play2fitness.com</div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex items-start gap-4"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-yellow text-brand-navy flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-brand-navy">Hours</div>
                  <div className="text-slate-600">
                    Programming: Mon–Fri · 3:00 – 6:00 PM
                    <br />
                    Inquiries answered within 24 hours.
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-6 md:p-10 border border-slate-200 shadow-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-brand-navy font-semibold">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your full name"
                    className="mt-2 h-12 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-brand-navy font-semibold">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 h-12 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-brand-navy font-semibold">
                    Phone (optional)
                  </Label>
                  <Input
                    id="phone"
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-2 h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="interest" className="text-brand-navy font-semibold">
                    I&apos;m interested in
                  </Label>
                  <Select
                    value={form.interest}
                    onValueChange={(v) => update("interest", v)}
                  >
                    <SelectTrigger
                      id="interest"
                      data-testid="contact-interest-select"
                      className="mt-2 h-12 rounded-xl"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General inquiry</SelectItem>
                      <SelectItem value="donate">Donating / sponsoring</SelectItem>
                      <SelectItem value="enroll">Enrolling my child</SelectItem>
                      <SelectItem value="volunteer">Volunteering</SelectItem>
                      <SelectItem value="partner">Partnership / business</SelectItem>
                      <SelectItem value="press">Press / media</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="subject" className="text-brand-navy font-semibold">
                  Subject *
                </Label>
                <Input
                  id="subject"
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                  placeholder="Quick summary"
                  className="mt-2 h-12 rounded-xl"
                  required
                />
              </div>

              <div className="mt-5">
                <Label htmlFor="message" className="text-brand-navy font-semibold">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={5}
                  placeholder="Tell us a bit about what you're looking for..."
                  className="mt-2 rounded-xl resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={submitting}
                className="mt-6 w-full sm:w-auto h-13 px-8 rounded-full bg-brand-navy hover:bg-slate-800 text-white font-bold text-base shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
